import { ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { F1065SchK1Service } from './f1065-sch-k1.service';
import { forkJoin, Subscription } from 'rxjs';
import { parseAsLocalDate } from '../../../shared/_utils/date-utils';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import { buildHeaderDataShell, generalConfigFromGlobal, normalizeFilterParams, rowPayloadFromActiveScreen } from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-f1065-sch-k1',
    templateUrl: './f1065-sch-k1.component.html',
    styleUrls: ['./f1065-sch-k1.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class F1065SchK1Component implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  baseURLs: any;
  headerData: any;
  rowData: any;
  filterParams: any;
  globalService: any;
  private readonly subs = new Subscription();

  // Modal state
  minimise = false;
  showFormLoader = true;
  showLine20Loader = true;
  showLine14Loader = true;
  isSaving = false;

  // Form data
  title = '';
  taxYear = 0;
  scenarioLabel = '';
  ISSUE_DESC = '';
  entityList: { [key: string]: any } = {};
  
  formattedScheduleKData: { [key: string]: any[] } = {};
  originalApiData: any[] = [];
  originalLine14Data: any[] = [];
  originalLine20Data: any[] = [];

  // Partner related
  arePartnerPresent = false;
  distinctLegalEntities: any[] = [];
  selectedGeneralPartner: any[] = [];

  // J section totals
  totalForJ: { [key: string]: number } = {
    'PROFIT(B)': 0,
    'LOSS(B)': 0,
    'CAPITAL(B)': 0,
    'PROFIT(E)': 0,
    'LOSS(E)': 0,
    'CAPITAL(E)': 0
  };

  // Line 14 data
  line14Data: { [key: string]: any[] } = {};
  line14DataOriginal: { [key: string]: any[] } = {};
  distinctCodes14: string[] = [];
  variances14: number[] = [];
  line14Total: number[] = [];
  firstLeidLine14 = '';
  tableRow14 = 0;

  // Line 20 data
  line20Data: { [key: string]: any[] } = {};
  line20DataOriginal: { [key: string]: any[] } = {};
  distinctCodes20: string[] = [];
  variances20: number[] = [];
  line20Total: number[] = [];
  firstLeidLine20 = '';
  tableRow20 = 0;

  // Date picker state
  datePickerState: { [key: number]: { startDate: boolean; endDate: boolean } } = {};

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private service: F1065SchK1Service,
    private cdr: ChangeDetectorRef,
    private _snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.initComponent();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.initComponent();
    }));
    this.initComponent();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.headerData = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.globalService = this.headerData;
    this.rowData = rowPayloadFromActiveScreen(activeScreen) || {};
    this.filterParams = normalizeFilterParams(activeScreen) || {};
  }

  private initComponent(): void {
    if (!this.baseURLs || !this.rowData || !Object.keys(this.rowData).length) {
      this.showFormLoader = false;
      this.showLine20Loader = false;
      this.showLine14Loader = false;
      return;
    }
    this.taxYear = this.rowData.TAX_YEAR;
    this.scenarioLabel = this.filterParams?.scenarioLabel || this.filterParams?.scenario_label || this.headerData?.scenarioLabel || 'Return As Filed';
    this.title = `Schedule K1 ( Tax Year : ${this.rowData.TAX_YEAR} - Scenario : ${this.scenarioLabel} )`;
    this.ISSUE_DESC = this.rowData.ISSUE_DESC || '';

    this.entityList = {
      'Tax Year': this.rowData.TAX_YEAR,
      'LEID': this.rowData.LEID,
      'LE Name': this.rowData.LE_NAME
    };

    this.loadData();
  }

  private loadData(): void {
    this.showFormLoader = true;
    this.showLine20Loader = true;
    this.showLine14Loader = true;

    const formData$ = this.service.loadFormData(
      this.baseURLs.base_url || this.baseURLs.api || '',
      this.rowData,
      this.globalService,
      this.filterParams
    );

    const line20Data$ = this.service.loadLine20Data(
      this.baseURLs.base_url || this.baseURLs.api || '',
      this.rowData,
      this.globalService,
      this.filterParams
    );

    forkJoin([formData$, line20Data$]).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: ([formResponse, line20Response]) => {
        this.showFormLoader = false;
        
        const formData = formResponse?.jsonObject || [];
        this.originalApiData = JSON.parse(JSON.stringify(formData));
        this.transformData(formData);

        this.showLine20Loader = false;
        this.showLine14Loader = false;

        const line20AllData = line20Response?.jsonObject || [];
        const line14Data = line20AllData.filter((item: any) => item.K1_ACCT?.startsWith('1065_K1_14'));
        const line20Data = line20AllData.filter((item: any) => item.K1_ACCT?.startsWith('1065_K1_20'));
        this.originalLine14Data = JSON.parse(JSON.stringify(line14Data));
        this.originalLine20Data = JSON.parse(JSON.stringify(line20Data));

        this.transformLine14Data(line14Data);
        this.transformLine20Data(line20Data);
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error loading data:', err);
        this.showFormLoader = false;
        this.showLine20Loader = false;
        this.showLine14Loader = false;
        this._snackBar.open('Error loading form data', 'Close', { duration: 5000, panelClass: ['error-snackbar'] });
        this.cdr.detectChanges();
      }
    });
  }

  private transformData(data: any[]): void {
    this.formattedScheduleKData = {};
    let generalSelectedLEID = '';

    for (let i = 0; i < data.length; i++) {
      const lineNo = data[i].LINE_NO;
      const lineData = data[i];

      if (lineNo === 'J1') {
        lineData['DECREASE'] = lineData['DECREASE'] === 'true';
      }

      if (lineNo === 'K1') {
        lineData['K1_FINAL'] = lineData['K1_FINAL'] === 'true';
        lineData['LIABILITY'] = lineData['LIABILITY'] === 'true';
        lineData['LIABILITY_GUARANTEE'] = lineData['LIABILITY_GUARANTEE'] === 'true';

        // Use parseAsLocalDate to avoid timezone shift issues
        lineData['TAXYEARBEGIN'] = parseAsLocalDate(lineData['TAXYEARBEGIN']);
        lineData['TAXYEAREND'] = parseAsLocalDate(lineData['TAXYEAREND']);
      }

      if (lineNo === 'M') {
        lineData['YES'] = lineData['YES'] === 'true';
        lineData['NO'] = lineData['NO'] === 'true';
      }

      if (this.taxYear <= 2020) {
        if (lineNo === '21' || lineNo === '22' || lineNo === 'D' || lineNo === 'G' || lineNo === 'G1') {
          lineData['ATTRIB_VALUE'] = lineData['ATTRIB_VALUE'] === 'true';
        }
      } else if (this.taxYear > 2020) {
        if (lineNo === '16' || lineNo === '22' || lineNo === '23' || lineNo === 'D' || lineNo === 'G' || lineNo === 'G1') {
          lineData['ATTRIB_VALUE'] = lineData['ATTRIB_VALUE'] === 'true';
        }
      }

      if (lineNo === 'H1') {
        lineData['ATTRIB_VALUE'] = lineData['DOMESTIC'] === 'true';
        lineData['DOMESTIC_EDIT_FLAG'] = lineData['DOMESTIC_EDIT_FLAG'] === 'true';
      }

      if (lineNo === 'H1(2)') {
        lineData['ATTRIB_VALUE'] = lineData['FOREIGN'] === 'true';
        lineData['FOREIGN_EDIT_FLAG'] = lineData['FOREIGN_EDIT_FLAG'] === 'true';
      }

      if (lineNo === 'H2') {
        lineData['ATTRIB_VALUE'] = lineData['DE'] === 'true';
      }

      if (lineNo === 'H2(1)') {
        lineData['ATTRIB_VALUE'] = lineData['TIN'];
      }

      if (lineNo === 'H2(2)') {
        lineData['ATTRIB_VALUE'] = lineData['NAME'];
      }

      if (lineNo === 'I1') {
        lineData['ATTRIB_VALUE'] = lineData['TYPE'];
      }

      if (lineNo === 'I2') {
        lineData['ATTRIB_VALUE'] = lineData['RETIREMENT'] === 'true';
      }

      if (this.formattedScheduleKData[lineNo]) {
        this.formattedScheduleKData[lineNo].push(lineData);
      } else {
        this.formattedScheduleKData[lineNo] = [lineData];
      }
    }

    this.distinctLegalEntities = this.getMultiselectOptionArray('G');
    this.arePartnerPresent = this.distinctLegalEntities.length > 0;

    this.distinctLegalEntities.forEach(le => {
      if (le.id === generalSelectedLEID) {
        this.selectedGeneralPartner.push(le);
      }
    });

    ['PROFIT(B)', 'LOSS(B)', 'CAPITAL(B)', 'PROFIT(E)', 'LOSS(E)', 'CAPITAL(E)'].forEach(key => {
      this.sumJCol(key);
    });

    // Initialize date picker state
    if (this.formattedScheduleKData['K1']) {
      this.formattedScheduleKData['K1'].forEach((_, idx) => {
        this.datePickerState[idx] = { startDate: false, endDate: false };
      });
    }
  }

  private transformLine14Data(data: any[]): void {
    this.line14Data = {};
    this.distinctCodes14 = [];

    if (!data || data.length === 0) {
      return;
    }

    for (let i = 0; i < data.length; i++) {
      this.firstLeidLine14 = data[0]['LEID'];
      data[i]['ADJ_AMT_1'] = Math.round(this.toNumber(data[i]['ADJ_AMT_1']));

      if (data[i]['LEID'] === this.firstLeidLine14) {
        this.distinctCodes14.push(data[i]['ATTRIB_TEXT_VALUE']);
      }

      if (!this.line14Data[data[i]['LEID']]) {
        this.line14Data[data[i]['LEID']] = [data[i]];
      } else {
        this.line14Data[data[i]['LEID']].push(data[i]);
      }
    }

    this.line14DataOriginal = JSON.parse(JSON.stringify(this.line14Data));
    this.tableRow14 = 4 + Object.keys(this.line14Data).length;
    this.line14ValueChanges();
  }

  private transformLine20Data(data: any[]): void {
    this.line20Data = {};
    this.distinctCodes20 = [];
    const copyMapper: { [key: string]: string } = {};

    if (!data || data.length === 0) {
      return;
    }

    let copyCounter = 0;
    for (let i = 0; i < data.length; i++) {
      this.firstLeidLine20 = data[0]['LEID'];
      data[i]['ADJ_AMT_1'] = Math.round(this.toNumber(data[i]['ADJ_AMT_1']));

      if (data[i]['LEID'] === this.firstLeidLine20) {
        this.distinctCodes20.push(data[i]['ATTRIB_TEXT_VALUE']);
      }

      if (!this.line20Data[data[i]['LEID']]) {
        this.line20Data[data[i]['LEID']] = [data[i]];
      } else {
        // RM-24504 logic
        if (copyMapper[data[i]['LE_NAME']] && this.line20Data[copyMapper[data[i]['LE_NAME']]][0]['LEID'] === data[i]['LEID']) {
          this.line20Data[copyMapper[data[i]['LE_NAME']]].push(data[i]);
        } else if (
          this.line20Data[data[i]['LEID']][0]['LEID'] === data[i]['LEID'] &&
          this.line20Data[data[i]['LEID']][0]['TP_KEY'] === data[i]['TP_KEY'] &&
          this.line20Data[data[i]['LEID']][0]['LE_NAME'] === data[i]['LE_NAME']
        ) {
          this.line20Data[data[i]['LEID']].push(data[i]);
        } else {
          this.line20Data[data[i]['LEID'] + `(${++copyCounter})`] = [data[i]];
          copyMapper[data[i].LE_NAME] = data[i]['LEID'] + `(${copyCounter})`;
        }
      }
    }

    this.line20DataOriginal = JSON.parse(JSON.stringify(this.line20Data));
    this.tableRow20 = 4 + Object.keys(this.line20Data).length;
    this.line20ValueChanges();
  }

  getMultiselectOptionArray(lineNumber: string): any[] {
    const multiselectArray: any[] = [];
    if (!this.formattedScheduleKData || !this.formattedScheduleKData[lineNumber]) {
      return multiselectArray;
    }

    this.formattedScheduleKData[lineNumber].forEach(element => {
      multiselectArray.push({
        id: element.LEID,
        lineNumber: lineNumber,
        label: element.LEID + ' - ' + element.LE_NAME
      });
    });

    return multiselectArray;
  }

  setLineValueForAll(key: string): void {
    const value = this.formattedScheduleKData[key][0].ATTRIB_VALUE;
    this.formattedScheduleKData[key].forEach(element => {
      element.ATTRIB_VALUE = value;
    });
  }

  unselectOther(index: number, type: string): void {
    if (type === 'YES') {
      this.formattedScheduleKData['M'][index]['NO'] = false;
    } else {
      this.formattedScheduleKData['M'][index]['YES'] = false;
    }
  }

  unselectOtherG(lineNumber: string, index: number): void {
    if (lineNumber === 'G') {
      this.formattedScheduleKData['G1'][index]['ATTRIB_VALUE'] = false;
      for (let i = 0; i < this.formattedScheduleKData['G1'].length; i++) {
        if (i !== index && !this.formattedScheduleKData['G'][i]['ATTRIB_VALUE']) {
          this.formattedScheduleKData['G'][i]['ATTRIB_VALUE'] = false;
          this.formattedScheduleKData['G1'][i]['ATTRIB_VALUE'] = true;
        }
      }
    } else {
      this.formattedScheduleKData['G'][index]['ATTRIB_VALUE'] = false;
    }
  }

  unselectOtherH(lineNumber: string, index: number): void {
    if (lineNumber === 'H1') {
      this.formattedScheduleKData['H1(2)'][index]['ATTRIB_VALUE'] = false;
    } else {
      this.formattedScheduleKData['H1'][index]['ATTRIB_VALUE'] = false;
    }
  }

  validationForJ(lineNumber: string, index: number, key: string): void {
    let value = '' + this.formattedScheduleKData[lineNumber][index][key];
    const parts = value.split('.');

    if (parts[1]) {
      const rhs = parts[1].substring(0, 6);
      this.formattedScheduleKData[lineNumber][index][key] = parts[0] + '.' + rhs;
    }

    this.sumJCol(key);
  }

  private sumJCol(key: string): void {
    let sum = 0;
    if (this.formattedScheduleKData['J1']) {
      for (let i = 0; i < this.formattedScheduleKData['J1'].length; i++) {
        sum += parseFloat(this.formattedScheduleKData['J1'][i][key] || '0') * 1000000;
      }
    }
    this.totalForJ[key] = parseFloat((sum / 1000000).toString());
  }

  checkK1Dates(lineNumber: string, index: number, dateType: string): void {
    const begin = this.formattedScheduleKData[lineNumber][index]['TAXYEARBEGIN'];
    const end = this.formattedScheduleKData[lineNumber][index]['TAXYEAREND'];
    
    if (begin > end && end) {
      this._snackBar.open('Value for Tax Year Begin cannot be greater than Tax Year End', 'Close', { duration: 5000, panelClass: ['error-snackbar'] });
    }
  }

  line14ValueChanges(): void {
    this.variances14 = Array.from({ length: this.distinctCodes14.length }, () => 0);
    this.line14Total = Array.from({ length: this.distinctCodes14.length }, () => 0);

    for (const leid in this.line14Data) {
      for (let index = 0; index < this.line14Data[leid].length; index++) {
        this.line14Total[index] += this.toNumber(this.line14Data[leid][index].ADJ_AMT_1);
        this.variances14[index] = this.line14Total[index] - this.toNumber(this.line14Data[leid][index]['CODE_VALUE']);

        if (this.line14Data[leid][index].ADJ_AMT_1 !== this.line14DataOriginal[leid]?.[index]?.ADJ_AMT_1 &&
            this.line14Data[leid][index].ADJ_AMT_2 === 0) {
          this.line14Data[leid][index].ADJ_AMT_2 = 1;
        }
      }
    }
  }

  line20ValueChanges(): void {
    this.variances20 = Array.from({ length: this.distinctCodes20.length }, () => 0);
    this.line20Total = Array.from({ length: this.distinctCodes20.length }, () => 0);

    for (const leid in this.line20Data) {
      for (let index = 0; index < this.line20Data[leid].length; index++) {
        this.line20Total[index] += this.toNumber(this.line20Data[leid][index].ADJ_AMT_1);
        this.variances20[index] = this.line20Total[index] - this.toNumber(this.line20Data[leid][index]['CODE_VALUE']);

        if (this.line20Data[leid][index].ADJ_AMT_1 !== this.line20DataOriginal[leid]?.[index]?.ADJ_AMT_1 &&
            this.line20Data[leid][index].ADJ_AMT_2 === 0) {
          this.line20Data[leid][index].ADJ_AMT_2 = 1;
        }
      }
    }
  }

  toggleDatePicker(index: number, field: 'startDate' | 'endDate'): void {
    if (!this.datePickerState[index]) {
      this.datePickerState[index] = { startDate: false, endDate: false };
    }
    this.datePickerState[index][field] = !this.datePickerState[index][field];
  }

  onDateChange(index: number, field: 'TAXYEARBEGIN' | 'TAXYEAREND', event: any): void {
    if (event) {
      // Use parseAsLocalDate to avoid timezone shift issues
      this.formattedScheduleKData['K1'][index][field] = parseAsLocalDate(event);
      this.checkK1Dates('K1', index, field);
    }
  }

  private convertDateToString(date: Date | null): string {
    if (date == null) return '';
    date = new Date(date);
    const mm = date.getMonth() + 1;
    const dd = date.getDate();

    return [(mm > 9 ? '' : '0') + mm + '/',
            (dd > 9 ? '' : '0') + dd + '/',
            date.getFullYear()].join('');
  }

  private isValidSaveJson(): boolean {
    let isValid = true;

    // Validate G/G1 selection
    if (this.formattedScheduleKData['G']) {
      this.formattedScheduleKData['G'].forEach((value, index) => {
        if (value.ATTRIB_VALUE === false && this.formattedScheduleKData['G1'][index].ATTRIB_VALUE === false) {
          this._snackBar.open('Type of Partner (General or Limited) must be selected under Item G', 'Close', { duration: 5000, panelClass: ['error-snackbar'] });
          isValid = false;
        }
      });
    }

    // Validate J totals = 100
    for (const key in this.totalForJ) {
      if (this.totalForJ[key] !== 100) {
        this._snackBar.open(`Sum of each column must be equal to 100 : ${key}`, 'Close', { duration: 5000, panelClass: ['error-snackbar'] });
        isValid = false;
      }
    }

    // Validate K1 dates
    if (this.formattedScheduleKData['K1']) {
      for (let i = 0; i < this.formattedScheduleKData['K1'].length; i++) {
        const elem = this.formattedScheduleKData['K1'][i];
        if (!elem['TAXYEARBEGIN'] || !elem['TAXYEAREND']) {
          this._snackBar.open('Date cannot be empty', 'Close', { duration: 5000, panelClass: ['error-snackbar'] });
          return false;
        }

        if (elem['TAXYEARBEGIN'] > elem['TAXYEAREND']) {
          this._snackBar.open(`Value for Tax Year Begin cannot be greater than Tax Year End : ${elem.LEID}`, 'Close', { duration: 5000, panelClass: ['error-snackbar'] });
          return false;
        }
      }
    }

    // Validate Line 14 variances
    if (!this.variances14) {
      this._snackBar.open('Line 14 - variance is null', 'Close', { duration: 5000, panelClass: ['error-snackbar'] });
      return false;
    }
    for (let i = 0; i < this.variances14.length; i++) {
      if (this.variances14[i] !== 0) {
        this._snackBar.open('Line 14 - variance is not zero', 'Close', { duration: 5000, panelClass: ['error-snackbar'] });
        return false;
      }
    }

    // Validate Line 20 variances
    if (!this.variances20) {
      this._snackBar.open('Line 20 - variance is null', 'Close', { duration: 5000, panelClass: ['error-snackbar'] });
      return false;
    }
    for (let i = 0; i < this.variances20.length; i++) {
      if (this.variances20[i] !== 0 && 
          !this.distinctCodes20[i]?.includes('Code AX1') && 
          !this.distinctCodes20[i]?.includes('Code AX2')) {
        this._snackBar.open('Line 20 - variance is not zero', 'Close', { duration: 5000, panelClass: ['error-snackbar'] });
        return false;
      }
    }

    return isValid;
  }

  private prepareSaveJson(): any[] {
    const saveJSON: any[] = [];

    if (!this.formattedScheduleKData['K1']) return saveJSON;

    for (let i = 0; i < this.formattedScheduleKData['K1'].length; i++) {
      saveJSON.push({
        COMBINATION_KEY: this.formattedScheduleKData['K1'][i].COMBINATION_KEY,
        LEID: this.formattedScheduleKData['K1'][i].LEID,
        LE_NAME: this.formattedScheduleKData['K1'][i].LE_NAME,
        VALUES: [],
        ALLOC_JSON: this.getAllLineSaveArray(this.formattedScheduleKData['K1'][i].LEID)
      });

      for (const lineNo in this.formattedScheduleKData) {
        const temp = JSON.parse(JSON.stringify(this.formattedScheduleKData[lineNo][i]));

        if (this.taxYear > 2020) {
          if (lineNo === '16' || lineNo === '23' || lineNo === '22' || lineNo === 'H1' || lineNo === 'H1(2)' || lineNo === 'H2' || lineNo === 'I2' || lineNo === 'G' || lineNo === 'G1') {
            temp.ATTRIB_VALUE += '';
          }
        } else if (this.taxYear <= 2020) {
          if (lineNo === '21' || lineNo === '22' || lineNo === 'H1' || lineNo === 'H1(2)' || lineNo === 'H2' || lineNo === 'I2' || lineNo === 'G' || lineNo === 'G1') {
            temp.ATTRIB_VALUE += '';
          }
        }

        if (lineNo === 'M') {
          const tempYes = JSON.parse(JSON.stringify(temp));
          tempYes.ATTRIB_NAME = 'YES';
          tempYes.ATTRIB_VALUE = '' + tempYes[tempYes.ATTRIB_NAME];

          const tempNo = JSON.parse(JSON.stringify(temp));
          tempNo.ATTRIB_NAME = 'NO';
          tempNo.ATTRIB_VALUE = '' + tempNo[tempNo.ATTRIB_NAME];

          saveJSON[i].VALUES.push(tempYes);
          saveJSON[i].VALUES.push(tempNo);
          continue;
        }

        if (lineNo === 'N') {
          let temp1 = JSON.parse(JSON.stringify(temp));
          temp1.ATTRIB_NAME = 'BEGINNING';
          temp1.ATTRIB_VALUE = temp1[temp1.ATTRIB_NAME];
          saveJSON[i].VALUES.push(temp1);

          temp1 = JSON.parse(JSON.stringify(temp));
          temp1.ATTRIB_NAME = 'ENDING';
          temp1.ATTRIB_VALUE = temp1[temp1.ATTRIB_NAME];
          saveJSON[i].VALUES.push(temp1);
          continue;
        }

        if (lineNo === 'J1') {
          let temp1 = JSON.parse(JSON.stringify(temp));
          temp1.ATTRIB_NAME = 'PROFIT(B)';
          temp1.ATTRIB_VALUE = temp1[temp1.ATTRIB_NAME];
          saveJSON[i].VALUES.push(temp1);

          temp1 = JSON.parse(JSON.stringify(temp));
          temp1.ATTRIB_NAME = 'LOSS(B)';
          temp1.ATTRIB_VALUE = temp1[temp1.ATTRIB_NAME];
          saveJSON[i].VALUES.push(temp1);

          temp1 = JSON.parse(JSON.stringify(temp));
          temp1.ATTRIB_NAME = 'CAPITAL(B)';
          temp1.ATTRIB_VALUE = temp1[temp1.ATTRIB_NAME];
          saveJSON[i].VALUES.push(temp1);

          temp1 = JSON.parse(JSON.stringify(temp));
          temp1.ATTRIB_NAME = 'PROFIT(E)';
          temp1.ATTRIB_VALUE = temp1[temp1.ATTRIB_NAME];
          saveJSON[i].VALUES.push(temp1);

          temp1 = JSON.parse(JSON.stringify(temp));
          temp1.ATTRIB_NAME = 'LOSS(E)';
          temp1.ATTRIB_VALUE = temp1[temp1.ATTRIB_NAME];
          saveJSON[i].VALUES.push(temp1);

          temp1 = JSON.parse(JSON.stringify(temp));
          temp1.ATTRIB_NAME = 'CAPITAL(E)';
          temp1.ATTRIB_VALUE = temp1[temp1.ATTRIB_NAME];
          saveJSON[i].VALUES.push(temp1);

          temp1 = JSON.parse(JSON.stringify(temp));
          temp1.ATTRIB_NAME = 'DECREASE';
          temp1.ATTRIB_VALUE = '' + temp1[temp1.ATTRIB_NAME];
          saveJSON[i].VALUES.push(temp1);

          continue;
        }

        if (lineNo === 'K1') {
          temp['TAXYEARBEGIN'] = this.convertDateToString(this.formattedScheduleKData[lineNo][i]['TAXYEARBEGIN']);
          temp['TAXYEAREND'] = this.convertDateToString(this.formattedScheduleKData[lineNo][i]['TAXYEAREND']);

          let temp1 = JSON.parse(JSON.stringify(temp));
          temp1.ATTRIB_NAME = 'NONRECOURSE';
          temp1.ATTRIB_VALUE = temp1[temp1.ATTRIB_NAME];
          saveJSON[i].VALUES.push(temp1);

          temp1 = JSON.parse(JSON.stringify(temp));
          temp1.ATTRIB_NAME = 'QUALIFIED';
          temp1.ATTRIB_VALUE = temp1[temp1.ATTRIB_NAME];
          saveJSON[i].VALUES.push(temp1);

          temp1 = JSON.parse(JSON.stringify(temp));
          temp1.ATTRIB_NAME = 'RECOURSE';
          temp1.ATTRIB_VALUE = temp1[temp1.ATTRIB_NAME];
          saveJSON[i].VALUES.push(temp1);

          temp1 = JSON.parse(JSON.stringify(temp));
          temp1.ATTRIB_NAME = 'NONRECOURSE_END';
          temp1.ATTRIB_VALUE = temp1[temp1.ATTRIB_NAME];
          saveJSON[i].VALUES.push(temp1);

          temp1 = JSON.parse(JSON.stringify(temp));
          temp1.ATTRIB_NAME = 'QUALIFIED_END';
          temp1.ATTRIB_VALUE = temp1[temp1.ATTRIB_NAME];
          saveJSON[i].VALUES.push(temp1);

          temp1 = JSON.parse(JSON.stringify(temp));
          temp1.ATTRIB_NAME = 'RECOURSE_END';
          temp1.ATTRIB_VALUE = temp1[temp1.ATTRIB_NAME];
          saveJSON[i].VALUES.push(temp1);

          temp1 = JSON.parse(JSON.stringify(temp));
          temp1.ATTRIB_NAME = 'LIABILITY';
          temp1.ATTRIB_VALUE = '' + temp1[temp1.ATTRIB_NAME];
          saveJSON[i].VALUES.push(temp1);

          temp1 = JSON.parse(JSON.stringify(temp));
          temp1.ATTRIB_NAME = 'LIABILITY_GUARANTEE';
          temp1.ATTRIB_VALUE = '' + temp1[temp1.ATTRIB_NAME];
          saveJSON[i].VALUES.push(temp1);

          temp1 = JSON.parse(JSON.stringify(temp));
          temp1.ATTRIB_NAME = 'K1_FINAL';
          temp1.ATTRIB_VALUE = '' + temp1[temp1.ATTRIB_NAME];
          saveJSON[i].VALUES.push(temp1);

          temp1 = JSON.parse(JSON.stringify(temp));
          temp1.ATTRIB_NAME = 'TAXYEARBEGIN';
          temp1.ATTRIB_VALUE = '' + temp1[temp1.ATTRIB_NAME];
          saveJSON[i].VALUES.push(temp1);

          temp1 = JSON.parse(JSON.stringify(temp));
          temp1.ATTRIB_NAME = 'TAXYEAREND';
          temp1.ATTRIB_VALUE = '' + temp1[temp1.ATTRIB_NAME];
          saveJSON[i].VALUES.push(temp1);
          continue;
        }

        saveJSON[i].VALUES.push(temp);
      }
    }

    return saveJSON;
  }

  private getLine14SaveArray(leid: string): any[] {
    return this.line14Data[leid] ? [...this.line14Data[leid]] : [];
  }

  private getLine20SaveArray(leid: string): any[] {
    return this.line20Data[leid] ? [...this.line20Data[leid]] : [];
  }

  private getAllLineSaveArray(leid: string): any[] {
    const allLineArray: any[] = [];

    const line14Values = this.getLine14SaveArray(leid);
    const line20Values = this.getLine20SaveArray(leid);

    for (const val of line14Values) {
      allLineArray.push(val);
    }
    for (const val of line20Values) {
      allLineArray.push(val);
    }

    return allLineArray;
  }

  save(): void {
    if (!this.isValidSaveJson()) {
      return;
    }

    const saveJson = this.prepareSaveJson();
    this.isSaving = true;

    this.service.saveScheduleK1Form(
      this.baseURLs.base_url || this.baseURLs.api || '',
      this.rowData,
      saveJson,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        this.isSaving = false;
        if (response.callSuccess === '1' && response.errorMessage === 'null') {
          this._snackBar.open('Transactions saved successfully', 'Close', { duration: 4000, panelClass: ['success-snackbar'] });
          this.requestGridUpdate.emit('refresh');
        } else {
          this._snackBar.open(response.errorMessage || 'Error saving data', 'Close', { duration: 5000, panelClass: ['error-snackbar'] });
        }
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.isSaving = false;
        console.error('Save error:', err);
        this._snackBar.open('Error saving data', 'Close', { duration: 5000, panelClass: ['error-snackbar'] });
        this.cdr.detectChanges();
      }
    });
  }

  reset(): void {
    this.formattedScheduleKData = {};
    this.selectedGeneralPartner = [];
    this.distinctLegalEntities = [];
    const data = JSON.parse(JSON.stringify(this.originalApiData));
    this.transformData(data);

    this.line14Data = JSON.parse(JSON.stringify(this.line14DataOriginal));
    this.line20Data = JSON.parse(JSON.stringify(this.line20DataOriginal));
    this.line14ValueChanges();
    this.line20ValueChanges();

    this.cdr.detectChanges();
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  toggleMinimize(): void {
    this.minimise = !this.minimise;
  }

  // Helper for template
  objectKeys(obj: any): string[] {
    return Object.keys(obj || {});
  }

  // Values can arrive from the API (or user input) already formatted,
  // e.g. "999,999" or "(45)", which Number() cannot parse.
  private toNumber(value: unknown): number {
    if (value === null || value === undefined || value === '') {
      return 0;
    }
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : 0;
    }

    const raw = String(value).trim();
    const isParenNegative = /^\(.*\)$/.test(raw);
    const parsed = Number(raw.replace(/[(),$\s]/g, ''));

    if (!Number.isFinite(parsed)) {
      return 0;
    }
    return isParenNegative ? -parsed : parsed;
  }

  // Format number with commas
  formatNumber(value: number | null): string {
    if (value === null || value === undefined) return '';
    return value.toLocaleString('en-US');
  }

  // Get code short label
  getCodeShortLabel(code: string): string {
    if (!code) return '';
    const parts = code.split(' ');
    return parts.length >= 2 ? `${parts[0]} ${parts[1]}` : code;
  }
}

